/**
 * Normalize analytics paths: strip tracking params, redact secrets, template IDs.
 * Used client-side and mirrored in track-analytics edge.
 */

export type MetricUnit = 'events' | 'sessions' | 'users' | 'bookings';

const TRACKING_PARAMS = new Set([
  'fbclid',
  'gclid',
  'gbraid',
  'wbraid',
  'msclkid',
  'srsltid',
  'mc_cid',
  'mc_eid',
  '_ga',
  '_gl',
]);

const SENSITIVE_PARAMS = new Set([
  'token',
  'access_token',
  'auth',
  'key',
  'secret',
  'password',
  'booking',
  'booking_token',
  'reset-token',
  'reset_token',
  'email',
  'recover',
  'code',
  'otp',
  'session',
  'sid',
]);

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isTrackingParam(name: string): boolean {
  const n = name.toLowerCase();
  if (TRACKING_PARAMS.has(n)) return true;
  if (n.startsWith('utm_')) return true;
  return false;
}

function isSensitiveParam(name: string): boolean {
  const n = name.toLowerCase();
  if (SENSITIVE_PARAMS.has(n)) return true;
  if (n.includes('token') || n.includes('secret') || n.includes('password')) return true;
  return false;
}

/** Apply route templates for known dynamic segments. */
export function applyRouteTemplate(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return '/';

  const out: string[] = [];
  for (let i = 0; i < parts.length; i++) {
    const seg = parts[i];
    const prev = parts[i - 1]?.toLowerCase();
    if (UUID_RE.test(seg)) {
      if (prev === 'booking' || prev === 'offert' || prev === 'quote') {
        out.push(':id');
        continue;
      }
      out.push(':id');
      continue;
    }
    out.push(seg);
  }
  return `/${out.join('/')}`;
}

/**
 * Normalize a path or path+search string for analytics storage.
 * Returns pathname only (no query), redacted and templated.
 */
export function normalizeAnalyticsPath(input: string | null | undefined): string {
  if (!input || typeof input !== 'string') return '/';

  let raw = input.trim();
  if (!raw) return '/';

  // Allow full URLs
  try {
    if (raw.startsWith('http://') || raw.startsWith('https://')) {
      const u = new URL(raw);
      raw = `${u.pathname}${u.search}`;
    }
  } catch {
    // keep raw
  }

  const hashIdx = raw.indexOf('#');
  if (hashIdx >= 0) raw = raw.slice(0, hashIdx);

  let pathname = raw;
  let search = '';
  const qIdx = raw.indexOf('?');
  if (qIdx >= 0) {
    pathname = raw.slice(0, qIdx) || '/';
    search = raw.slice(qIdx + 1);
  }

  if (!pathname.startsWith('/')) pathname = `/${pathname}`;

  // Drop all query params from stored path (tracking + sensitive).
  // Attribution params are captured separately via attribution helpers.
  void search;

  // Collapse duplicate slashes
  pathname = pathname.replace(/\/{2,}/g, '/');
  if (pathname.length > 1 && pathname.endsWith('/')) {
    pathname = pathname.slice(0, -1);
  }

  return applyRouteTemplate(pathname);
}

/** True if raw path contained params we strip (for DQ counters). */
export function pathHadStrippedParams(input: string | null | undefined): boolean {
  if (!input) return false;
  const qIdx = input.indexOf('?');
  if (qIdx < 0) return false;
  const search = input.slice(qIdx + 1);
  if (!search) return false;
  try {
    const params = new URLSearchParams(search);
    for (const key of params.keys()) {
      if (isTrackingParam(key) || isSensitiveParam(key)) return true;
      // Any query on analytics path is stripped
      return true;
    }
  } catch {
    return true;
  }
  return false;
}

export function frictionDataConfidence(views: number): 'low' | 'medium' | 'high' {
  if (views < 10) return 'low';
  if (views < 30) return 'medium';
  return 'high';
}
